/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Module Switcher Pattern Demo
 *
 * Interactive demo showcasing 3 navigation patterns for multi-module SaaS.
 * Temporary page — delete after choosing a pattern.
 */

import { useState } from 'react';
import {
  Package, Users, Calculator, FolderKanban,
  FolderTree, BarChart3, MapPin, ArrowRightLeft,
  Settings, Ruler, ChevronDown, Check,
  AlertTriangle, Grid3X3, SlidersHorizontal,
  Briefcase, Receipt, Building2, ChevronRight,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ─── Shared data ────────────────────────────────────────────────────

interface Module {
  id: string;
  name: string;
  shortName: string;
  icon: LucideIcon;
  color: string;
  sections: { title: string; items: { label: string; icon: LucideIcon; badge?: number; badgeColor?: string }[] }[];
}

const MODULES: Module[] = [
  {
    id: 'venta',
    name: 'VentaCoreX',
    shortName: 'VC',
    icon: Package,
    color: '#dc2626',
    sections: [
      {
        title: 'INVENTORY',
        items: [
          { label: 'Products', icon: Package },
          { label: 'Categories', icon: FolderTree },
          { label: 'Stock Levels', icon: BarChart3 },
          { label: 'Stock Adjust', icon: SlidersHorizontal },
          { label: 'Low Stock Alerts', icon: AlertTriangle, badge: 3, badgeColor: '#ef4444' },
        ],
      },
      {
        title: 'WAREHOUSE',
        items: [
          { label: 'Locations', icon: MapPin },
          { label: 'Stock Matrix', icon: Grid3X3 },
          { label: 'Transfers', icon: ArrowRightLeft },
        ],
      },
      {
        title: 'CONFIGURATION',
        items: [
          { label: 'Units of Measure', icon: Ruler },
          { label: 'Settings', icon: Settings },
        ],
      },
    ],
  },
  {
    id: 'hr',
    name: 'HRCoreX',
    shortName: 'HR',
    icon: Users,
    color: '#2563eb',
    sections: [
      {
        title: 'EMPLOYEES',
        items: [
          { label: 'Directory', icon: Users },
          { label: 'Departments', icon: Building2 },
          { label: 'Attendance', icon: Briefcase },
        ],
      },
      {
        title: 'CONFIGURATION',
        items: [
          { label: 'Settings', icon: Settings },
        ],
      },
    ],
  },
  {
    id: 'compta',
    name: 'ComptaCoreX',
    shortName: 'CX',
    icon: Calculator,
    color: '#16a34a',
    sections: [
      {
        title: 'ACCOUNTING',
        items: [
          { label: 'Invoices', icon: Receipt },
          { label: 'Expenses', icon: Calculator },
        ],
      },
      {
        title: 'CONFIGURATION',
        items: [
          { label: 'Settings', icon: Settings },
        ],
      },
    ],
  },
  {
    id: 'pm',
    name: 'PMCoreX',
    shortName: 'PM',
    icon: FolderKanban,
    color: '#9333ea',
    sections: [
      {
        title: 'PROJECTS',
        items: [
          { label: 'Board', icon: FolderKanban },
          { label: 'Tasks', icon: FolderTree },
        ],
      },
    ],
  },
];

// ─── Pattern A: Icon Rail + Sidebar ─────────────────────────────────

function PatternIconRail() {
  const [active, setActive] = useState('venta');
  const mod = MODULES.find((m) => m.id === active)!;

  return (
    <div style={{ display: 'flex', height: '100%', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--color-border-default)' }}>
      {/* Icon Rail */}
      <div style={{
        width: 56,
        background: 'var(--color-bg-primary)',
        borderRight: '1px solid var(--color-border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 12,
        gap: 4,
        flexShrink: 0,
      }}>
        {MODULES.map((m) => {
          const isActive = m.id === active;
          return (
            <button
              key={m.id}
              onClick={() => setActive(m.id)}
              title={m.name}
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isActive ? m.color : 'transparent',
                color: isActive ? '#fff' : 'var(--color-text-muted)',
                transition: 'all 0.2s',
                position: 'relative',
              }}
            >
              <m.icon size={20} />
              {isActive && (
                <span style={{
                  position: 'absolute',
                  left: -2,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 4,
                  height: 20,
                  borderRadius: 4,
                  background: m.color,
                }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Module Sidebar */}
      <div style={{
        width: 200,
        background: 'var(--color-bg-secondary)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}>
        {/* Module header */}
        <div style={{
          padding: '14px 14px 10px',
          borderBottom: '1px solid var(--color-border-subtle)',
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text-primary)' }}>{mod.name}</div>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>Inventory & Sales</div>
        </div>

        {/* Nav sections */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          {mod.sections.map((section) => (
            <div key={section.title} style={{ marginBottom: 4 }}>
              <div style={{
                padding: '6px 14px',
                fontSize: 10.5,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--color-text-muted)',
              }}>
                {section.title}
              </div>
              {section.items.map((item, i) => (
                <div
                  key={item.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '7px 14px 7px 18px',
                    fontSize: 13,
                    color: i === 0 && section === mod.sections[0] ? mod.color : 'var(--color-text-secondary)',
                    background: i === 0 && section === mod.sections[0] ? `${mod.color}12` : 'transparent',
                    borderLeft: i === 0 && section === mod.sections[0] ? `2px solid ${mod.color}` : '2px solid transparent',
                    fontWeight: i === 0 && section === mod.sections[0] ? 500 : 400,
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  <item.icon size={15} style={{ opacity: 0.7 }} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge && (
                    <span style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: 99,
                      background: `${item.badgeColor}22`,
                      color: item.badgeColor,
                    }}>
                      {item.badge}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Content preview */}
      <div style={{ flex: 1, background: 'var(--color-bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
          <mod.icon size={48} style={{ opacity: 0.2, margin: '0 auto 12px' }} />
          <div style={{ fontSize: 14 }}>{mod.name} Content Area</div>
        </div>
      </div>
    </div>
  );
}

// ─── Pattern B: Top Module Bar ──────────────────────────────────────

function PatternTopBar() {
  const [active, setActive] = useState('venta');
  const mod = MODULES.find((m) => m.id === active)!;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--color-border-default)' }}>
      {/* Top module bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: '0 12px',
        height: 44,
        background: 'var(--color-bg-secondary)',
        borderBottom: '1px solid var(--color-border-subtle)',
        flexShrink: 0,
      }}>
        {MODULES.map((m) => {
          const isActive = m.id === active;
          return (
            <button
              key={m.id}
              onClick={() => setActive(m.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '6px 14px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                background: isActive ? `${m.color}15` : 'transparent',
                color: isActive ? m.color : 'var(--color-text-muted)',
                transition: 'all 0.2s',
                position: 'relative',
              }}
            >
              <m.icon size={16} />
              <span>{m.name}</span>
              {isActive && (
                <span style={{
                  position: 'absolute',
                  bottom: -7,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '60%',
                  height: 2,
                  borderRadius: 2,
                  background: m.color,
                }} />
              )}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Module Sidebar */}
        <div style={{
          width: 210,
          background: 'var(--color-bg-secondary)',
          borderRight: '1px solid var(--color-border-subtle)',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
            {mod.sections.map((section) => (
              <div key={section.title} style={{ marginBottom: 4 }}>
                <div style={{
                  padding: '6px 14px',
                  fontSize: 10.5,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--color-text-muted)',
                }}>
                  {section.title}
                </div>
                {section.items.map((item, i) => (
                  <div
                    key={item.label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '7px 14px 7px 18px',
                      fontSize: 13,
                      color: i === 0 && section === mod.sections[0] ? mod.color : 'var(--color-text-secondary)',
                      background: i === 0 && section === mod.sections[0] ? `${mod.color}12` : 'transparent',
                      borderLeft: i === 0 && section === mod.sections[0] ? `2px solid ${mod.color}` : '2px solid transparent',
                      fontWeight: i === 0 && section === mod.sections[0] ? 500 : 400,
                      cursor: 'pointer',
                    }}
                  >
                    <item.icon size={15} style={{ opacity: 0.7 }} />
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {item.badge && (
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '1px 6px',
                        borderRadius: 99,
                        background: `${item.badgeColor}22`,
                        color: item.badgeColor,
                      }}>
                        {item.badge}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Content preview */}
        <div style={{ flex: 1, background: 'var(--color-bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
            <mod.icon size={48} style={{ opacity: 0.2, margin: '0 auto 12px' }} />
            <div style={{ fontSize: 14 }}>{mod.name} Content Area</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Pattern C: Sidebar Dropdown Switcher ───────────────────────────

function PatternDropdown() {
  const [active, setActive] = useState('venta');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const mod = MODULES.find((m) => m.id === active)!;

  return (
    <div style={{ display: 'flex', height: '100%', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--color-border-default)' }}>
      {/* Sidebar with dropdown */}
      <div style={{
        width: 220,
        background: 'var(--color-bg-secondary)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        position: 'relative',
      }}>
        {/* Module Switcher Dropdown Header */}
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 14px',
            borderBottom: '1px solid var(--color-border-subtle)',
            background: 'none',
            border: 'none',
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
            borderBottomColor: 'var(--color-border-subtle)',
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
          }}
        >
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: mod.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            flexShrink: 0,
          }}>
            <mod.icon size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text-primary)' }}>{mod.name}</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Inventory & Sales</div>
          </div>
          <ChevronDown
            size={16}
            style={{
              color: 'var(--color-text-muted)',
              transition: 'transform 0.2s',
              transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </button>

        {/* Dropdown panel */}
        {dropdownOpen && (
          <div style={{
            position: 'absolute',
            top: 60,
            left: 8,
            right: 8,
            background: 'var(--color-bg-tertiary)',
            borderRadius: 10,
            border: '1px solid var(--color-border-default)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 100,
            padding: 4,
            animation: 'fadeIn 0.15s ease',
          }}>
            <div style={{ padding: '6px 10px 4px', fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)' }}>
              Switch Module
            </div>
            {MODULES.map((m) => {
              const isActive = m.id === active;
              return (
                <button
                  key={m.id}
                  onClick={() => { setActive(m.id); setDropdownOpen(false); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: 'none',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    background: isActive ? `${m.color}15` : 'transparent',
                    color: isActive ? m.color : 'var(--color-text-secondary)',
                    fontWeight: isActive ? 600 : 400,
                    fontSize: 13,
                    transition: 'background 0.15s',
                  }}
                >
                  <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: isActive ? m.color : 'var(--color-bg-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isActive ? '#fff' : 'var(--color-text-muted)',
                    flexShrink: 0,
                  }}>
                    <m.icon size={15} />
                  </div>
                  <span style={{ flex: 1 }}>{m.name}</span>
                  {isActive && <Check size={14} />}
                </button>
              );
            })}
          </div>
        )}

        {/* Nav sections */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          {mod.sections.map((section) => (
            <div key={section.title} style={{ marginBottom: 4 }}>
              <div style={{
                padding: '6px 14px',
                fontSize: 10.5,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--color-text-muted)',
              }}>
                {section.title}
              </div>
              {section.items.map((item, i) => (
                <div
                  key={item.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '7px 14px 7px 18px',
                    fontSize: 13,
                    color: i === 0 && section === mod.sections[0] ? mod.color : 'var(--color-text-secondary)',
                    background: i === 0 && section === mod.sections[0] ? `${mod.color}12` : 'transparent',
                    borderLeft: i === 0 && section === mod.sections[0] ? `2px solid ${mod.color}` : '2px solid transparent',
                    fontWeight: i === 0 && section === mod.sections[0] ? 500 : 400,
                    cursor: 'pointer',
                  }}
                >
                  <item.icon size={15} style={{ opacity: 0.7 }} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge && (
                    <span style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: 99,
                      background: `${item.badgeColor}22`,
                      color: item.badgeColor,
                    }}>
                      {item.badge}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Content preview */}
      <div style={{ flex: 1, background: 'var(--color-bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
          <mod.icon size={48} style={{ opacity: 0.2, margin: '0 auto 12px' }} />
          <div style={{ fontSize: 14 }}>{mod.name} Content Area</div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Demo Page ─────────────────────────────────────────────────

export default function ModuleSwitcherDemo() {
  const [selected, setSelected] = useState<'rail' | 'topbar' | 'dropdown'>('rail');

  const patterns = [
    {
      id: 'rail' as const,
      title: 'Pattern A: Icon Rail + Sidebar',
      description: 'Narrow icon strip on the far left with one icon per module. The active module\'s sidebar sits next to it. Most scalable — works for 2-10 modules without eating horizontal space.',
      pros: ['Scales to many modules', 'Always visible', 'Minimal space (~48px)', 'Industry standard (Slack, Discord, Teams)'],
      cons: ['Slightly more complex layout', 'Icons need to be recognizable'],
    },
    {
      id: 'topbar' as const,
      title: 'Pattern B: Top Module Bar',
      description: 'Horizontal tab bar above the sidebar showing all licensed modules. Each tab has icon + name. Feels like browser tabs.',
      pros: ['Very discoverable', 'Shows module names', 'Familiar tab pattern', 'Easy to implement'],
      cons: ['Takes vertical space (44px)', 'Doesn\'t scale past 5-6 modules', 'Module names can get long'],
    },
    {
      id: 'dropdown' as const,
      title: 'Pattern C: Sidebar Dropdown',
      description: 'The sidebar header doubles as a module switcher dropdown (like Slack workspace switcher). Click to reveal all available modules.',
      pros: ['Minimal UI footprint', 'No extra columns or rows', 'Familiar pattern', 'Same sidebar width as current'],
      cons: ['Switcher is hidden (less discoverable)', 'Extra click required', 'Easy to miss for new users'],
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{
          fontSize: 24,
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          fontFamily: 'var(--font-heading)',
          marginBottom: 6,
        }}>
          Module Switcher Patterns
        </h1>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          Choose how users navigate between licensed modules (VENTA, HR, Compta, PM).
          Click each pattern tab, then interact with the demo below. All demos are fully theme-aware.
        </p>
      </div>

      {/* Pattern selector tabs */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 20,
        flexWrap: 'wrap',
      }}>
        {patterns.map((p) => {
          const isActive = selected === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setSelected(p.id)}
              style={{
                padding: '10px 18px',
                borderRadius: 10,
                border: `2px solid ${isActive ? 'var(--color-brand-primary)' : 'var(--color-border-default)'}`,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: isActive ? 700 : 500,
                background: isActive ? 'var(--color-brand-primary)' : 'var(--color-bg-secondary)',
                color: isActive ? '#fff' : 'var(--color-text-secondary)',
                transition: 'all 0.2s',
              }}
            >
              {p.title}
            </button>
          );
        })}
      </div>

      {/* Description + Pros/Cons */}
      {patterns.filter((p) => p.id === selected).map((p) => (
        <div key={p.id} style={{
          marginBottom: 20,
          padding: 16,
          background: 'var(--color-bg-secondary)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--color-border-subtle)',
        }}>
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 12, lineHeight: 1.5 }}>
            {p.description}
          </p>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#16a34a', marginBottom: 6 }}>Pros</div>
              {p.pros.map((pro) => (
                <div key={pro} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--color-text-secondary)', marginBottom: 3 }}>
                  <ChevronRight size={12} style={{ color: '#16a34a' }} /> {pro}
                </div>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#ef4444', marginBottom: 6 }}>Cons</div>
              {p.cons.map((con) => (
                <div key={con} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--color-text-secondary)', marginBottom: 3 }}>
                  <ChevronRight size={12} style={{ color: '#ef4444' }} /> {con}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Interactive Demo */}
      <div style={{
        height: 480,
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}>
        {selected === 'rail' && <PatternIconRail />}
        {selected === 'topbar' && <PatternTopBar />}
        {selected === 'dropdown' && <PatternDropdown />}
      </div>

      <p style={{ marginTop: 16, fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', fontStyle: 'italic' }}>
        Click on different module icons/tabs/dropdown to see the sidebar update. This is a demo page — delete after choosing.
      </p>
    </div>
  );
}
